/**
 * GM app drawer: preview swap + subpanel navigation (specs 0028, 0044).
 */
function bootGmDrawer() {
  const drawer = document.querySelector('.menu-drawer--gm-static');
  if (!(drawer instanceof HTMLElement)) return;

  /* Flag fora do DOM: a hidratação do header (morph) apaga atributos. */
  window.__gmDrawerBooted ??= new WeakSet();
  if (window.__gmDrawerBooted.has(drawer)) return;

  const drawerRoot = drawer.closest('details.menu-drawer-container') ?? drawer.closest('header-drawer');
  if (!drawerRoot) return;
  window.__gmDrawerBooted.add(drawer);

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const panelTransitionMs = reduceMotion ? 0 : 300;

  mountDrawerBlocks(drawer);
  initDesktopScrollLockFix(drawer);
  applyEditorPreviewSrcs(drawer);
  const preview = initPreview(drawer, reduceMotion);
  const panels = initPanels(drawer, drawerRoot, panelTransitionMs, preview);
  initDrawerSearch(drawer);
  initDesktopNavTriggers(panels.openPanel);
  watchEditorPreviewSrcs(drawer);

  window.GmDrawer = {
    openPanel: panels.openPanel,
    close: panels.closeDrawer,
  };
}

bootGmDrawer();

/* Editor: o header re-renderiza (novo DOM, sem a flag) → monta e liga de novo. */
if (!window.__gmDrawerEditorHooks) {
  window.__gmDrawerEditorHooks = true;
  document.addEventListener('shopify:section:load', () => bootGmDrawer());
  document.addEventListener('shopify:block:select', (event) => revealDrawerBlock(event.target));
}

/**
 * Spec 0132: os blocos do drawer se renderizam em `#gm-drawer-native-previews`
 * (staging escondido), dentro de `<template data-gm-drawer-part>`. Aqui eles vão
 * para o lugar: itens raiz → `ul.menu-drawer__menu`, camadas → `.gm-drawer-layers`.
 * Camada dentro de item/card vira filha dele (pai vem do DOM, sem handle).
 * @param {HTMLElement} drawer
 */
function mountDrawerBlocks(drawer) {
  const source = document.getElementById('gm-drawer-native-previews');
  if (!source || source.getAttribute('data-gm-drawer-source') !== 'blocos') return;

  const list = drawer.querySelector('.menu-drawer__menu');
  const layers = drawer.querySelector('.gm-drawer-layers');
  if (!list || !layers) return;

  const dividerClass = source.getAttribute('data-gm-divider-class') || '';
  let animationIndex = 0;

  /* Cópias do <li> desktop que o item raiz também emite aqui. */
  source.querySelectorAll(':scope > li').forEach((li) => li.remove());

  [...source.children].forEach((node) => {
    if (!(node instanceof HTMLTemplateElement)) return;
    const part = node.getAttribute('data-gm-drawer-part');

    if (part === 'panel') {
      mountDrawerPanel(node, layers, null);
      return;
    }
    if (part !== 'root') return;

    const fragment = /** @type {DocumentFragment} */ (node.content.cloneNode(true));
    /* `:scope` não casa em DocumentFragment: filtra os filhos diretos. */
    const children = [...fragment.children];
    const item = children.find((child) => child.tagName === 'LI');
    const panelTemplates = children.filter(
      (child) => child instanceof HTMLTemplateElement && child.getAttribute('data-gm-drawer-part') === 'panel'
    );

    if (item instanceof HTMLElement) {
      animationIndex += 1;
      item.style.setProperty('--menu-drawer-animation-index', String(animationIndex));
      if (dividerClass) item.classList.add(dividerClass);
      list.append(item);
    }

    panelTemplates.forEach((template, index) => {
      const handle = mountDrawerPanel(template, layers, null);
      if (index !== 0 || !handle) return;
      linkDrawerTrigger(item?.querySelector('.menu-drawer__menu-item'), handle);
      const rootId = baseBlockId(node.getAttribute('data-gm-root-id'));
      if (rootId) gmRootPanels().set(rootId, handle);
    });
  });
}

/**
 * Item raiz (id sem sufixo) → handle da camada filha. A barra desktop consulta
 * no clique: o atributo vem vazio do servidor e a hidratação apagaria o do JS.
 * Global via window: o script roda antes desta linha e pode ser reexecutado.
 * @returns {Map<string, string>}
 */
function gmRootPanels() {
  window.__gmRootPanels ??= new Map();
  return window.__gmRootPanels;
}

/**
 * @param {Element} trigger - Link da barra desktop
 * @returns {string | null}
 */
function desktopTriggerPanel(trigger) {
  const direct = trigger.getAttribute('data-gm-desktop-drawer-panel');
  if (direct) return direct;
  const rootId = baseBlockId(trigger.closest('[data-gm-root-id]')?.getAttribute('data-gm-root-id') ?? null);
  return (rootId && gmRootPanels().get(rootId)) || null;
}

/**
 * O mesmo bloco renderizado de novo (staging do drawer × barra desktop) ganha
 * sufixo `-1`, `-2` no `block.id`. Compara sem ele.
 * @param {string | null} id
 */
function baseBlockId(id) {
  return (id || '').replace(/-\d+$/, '');
}

/**
 * @param {HTMLTemplateElement} template
 * @param {Element} layers
 * @param {string | null} parentHandle
 * @returns {string | null} handle da camada montada
 */
function mountDrawerPanel(template, layers, parentHandle) {
  const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
  const panel = fragment.querySelector('.gm-drawer-subpanel');
  if (!(panel instanceof HTMLElement)) return null;

  const handle = panel.getAttribute('data-gm-drawer-panel');
  if (!handle) return null;
  if (parentHandle) {
    panel.setAttribute('data-gm-drawer-panel-parent', parentHandle);
    panel.classList.add('gm-drawer-subpanel--nested');
  }
  layers.append(panel);

  panel.querySelectorAll('template[data-gm-drawer-part="panel"]').forEach((child) => {
    const card = child.closest('[data-gm-drawer-card]');
    const childHandle = mountDrawerPanel(/** @type {HTMLTemplateElement} */ (child), layers, handle);
    child.remove();
    if (card && childHandle) linkDrawerTrigger(card.querySelector('.gm-drawer-card'), childHandle);
  });

  return handle;
}

/**
 * @param {Element | null | undefined} trigger
 * @param {string} handle
 */
function linkDrawerTrigger(trigger, handle) {
  if (!(trigger instanceof HTMLElement)) return;
  trigger.setAttribute('data-gm-drawer-panel-open', handle);
  trigger.setAttribute('aria-controls', `gm-drawer-panel-${handle}`);
  trigger.setAttribute('aria-expanded', 'false');
}

/**
 * Editor: bloco selecionado dentro do drawer → abre o drawer (e a camada).
 * @param {EventTarget | null} target
 */
function revealDrawerBlock(target) {
  if (!(target instanceof Element)) return;
  const drawer = target.closest('.menu-drawer--gm-static');
  if (!(drawer instanceof HTMLElement)) return;

  const panel = target.closest('.gm-drawer-subpanel');
  const handle = panel?.getAttribute('data-gm-drawer-panel');
  if (handle && window.GmDrawer) {
    window.GmDrawer.openPanel(handle);
    return;
  }
  ensureDrawerOpen(drawer);
}

/** Apply item-picker URLs onto drawer hover targets, including hardcoded SALDO/SHOP ids. */
function applyEditorPreviewSrcs(drawer) {
  /** @type {Record<string, string>} */
  const previews = {};

  const nativeRoot = document.getElementById('gm-drawer-native-previews');
  if (nativeRoot) {
    nativeRoot.querySelectorAll('[data-gm-native-preview-key]').forEach((el) => {
      const key = (el.getAttribute('data-gm-native-preview-key') || '').trim();
      const src = (el.getAttribute('data-gm-native-preview-src') || '').trim().replace(/&amp;/g, '&');
      if (key && src) previews[key] = src;
    });
  }

  const attrHovers = {
    shop: drawer.getAttribute('data-hover-shop') || '',
    sobre: drawer.getAttribute('data-hover-sobre') || '',
    'collab-elo': drawer.getAttribute('data-hover-collabs') || '',
    saldo: drawer.getAttribute('data-hover-saldo') || '',
  };
  Object.keys(attrHovers).forEach((key) => {
    const src = attrHovers[key].trim().replace(/&amp;/g, '&');
    if (src) previews[key] = src;
  });

  const jsonEl = document.getElementById('gm-drawer-previews');
  if (jsonEl) {
    try {
      const parsed = JSON.parse(jsonEl.textContent || '{}');
      if (parsed && typeof parsed === 'object') {
        Object.assign(previews, parsed);
      }
    } catch (e) {
      /* ignore invalid json */
    }
  }

  const rawMap = drawer.getAttribute('data-gm-preview-map') || '';
  if (rawMap) {
    rawMap.split(/¶|&para;|&#182;/).forEach((row) => {
      const sepMatch = row.match(/§|&sect;|&#167;/);
      if (!sepMatch || sepMatch.index < 1) return;
      const key = row.slice(0, sepMatch.index).trim();
      const src = row.slice(sepMatch.index + sepMatch[0].length).trim().replace(/&amp;/g, '&');
      if (key && src) previews[key] = src;
    });
  }

  const fallbackIds = {
    shop: 'HeaderDrawer-gm-shop',
    sobre: 'HeaderDrawer-gm-sobre',
    'collab-elo': 'HeaderDrawer-gm-collabs',
    collabs: 'HeaderDrawer-gm-collabs',
    saldo: 'HeaderDrawer-gm-saldo',
  };

  Object.keys(previews).forEach((key) => {
    const src = previews[key];
    if (!src) return;

    drawer.querySelectorAll(`[data-gm-preview-key="${CSS.escape(key)}"]`).forEach((item) => {
      setPreviewSrc(item, src);
    });

    const fallbackId = fallbackIds[key] || `HeaderDrawer-gm-${key}`;
    const fallbackEl =
      drawer.querySelector(`#${CSS.escape(fallbackId)}`) || document.getElementById(fallbackId);
    if (fallbackEl) setPreviewSrc(fallbackEl, src);
  });
}

function setPreviewSrc(el, src) {
  if (!el || !src) return;
  if (el.getAttribute('data-preview-src') === src) return;
  el.setAttribute('data-preview-src', src);
}

/** Header morph / Section Rendering API can replace SALDO after first JS run. */
function watchEditorPreviewSrcs(drawer) {
  const rerun = () => applyEditorPreviewSrcs(drawer);
  requestAnimationFrame(rerun);
  window.setTimeout(rerun, 0);
  window.setTimeout(rerun, 250);
  window.setTimeout(rerun, 1000);

  document.addEventListener('shopify:section:load', rerun);
  document.addEventListener('shopify:section:reorder', rerun);
  document.addEventListener('shopify:block:select', rerun);

  const nav = drawer.querySelector('.menu-drawer__navigation');
  if (!nav || typeof MutationObserver !== 'function') return;

  const observer = new MutationObserver(rerun);
  observer.observe(nav, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-preview-src'],
  });
}

/** Desktop: drawer lateral — não trava scroll da página (evita sumir scrollbar / layout shift). */
function initDesktopScrollLockFix(drawer) {
  const details = drawer.closest('details.menu-drawer-container');
  if (!(details instanceof HTMLDetailsElement) || !details.hasAttribute('scroll-lock')) return;

  const desktopMq = window.matchMedia('(min-width: 750px)');

  function releaseScrollLock() {
    if (!desktopMq.matches) return;
    document.documentElement.removeAttribute('scroll-lock');
  }

  details.addEventListener('toggle', () => {
    if (!details.open) return;
    requestAnimationFrame(releaseScrollLock);
  });

  desktopMq.addEventListener('change', () => {
    if (details.open) releaseScrollLock();
  });
}

/**
 * @param {HTMLElement} drawer
 * @param {boolean} reduceMotion
 * @returns {{ freezePreview: () => void, restoreFrozenPreview: () => void, clearPreviewTimers: () => void }}
 */
function initPreview(drawer, reduceMotion) {
  const noopPreview = {
    freezePreview: () => {},
    restoreFrozenPreview: () => {},
    clearPreviewTimers: () => {},
  };

  const nav = drawer.querySelector('.menu-drawer__navigation');
  const artStack = drawer.querySelector('.menu-drawer__bottom-art-stack');
  if (!nav || !artStack) return noopPreview;

  const images = /** @type {HTMLImageElement[]} */ ([...artStack.querySelectorAll('.menu-drawer__bottom-art-image')]);
  if (images.length < 2) return noopPreview;

  const defaultSrc = artStack.dataset.defaultSrc || images[0].src;
  const transitionMs = reduceMotion ? 0 : 300;

  /** @type {number} */
  let visibleIndex = 0;
  /** @type {number | undefined} */
  let swapTimer;
  /** @type {string | null} */
  let frozenPreviewSrc = null;
  let suppressPreviewEvents = false;

  const activeLink = nav.querySelector('.menu-drawer__menu-item--active[data-preview-src]');
  const activePreviewSrc = activeLink?.getAttribute('data-preview-src') || '';

  function getVisibleSrc() {
    const img = images[visibleIndex];
    return img?.currentSrc || img?.src || defaultSrc;
  }

  function clearPreviewTimers() {
    clearTimeout(swapTimer);
  }

  function freezePreview() {
    clearPreviewTimers();
    frozenPreviewSrc = getVisibleSrc();
  }

  function restoreFrozenPreview() {
    clearPreviewTimers();
    suppressPreviewEvents = true;
    if (frozenPreviewSrc) {
      setPreview(frozenPreviewSrc);
    }
    frozenPreviewSrc = null;
    window.setTimeout(() => {
      suppressPreviewEvents = false;
    }, 120);
  }

  /**
   * @param {string} src
   */
  function setPreview(src) {
    const targetSrc = src || defaultSrc;
    const current = images[visibleIndex];

    if (current.src === targetSrc || current.getAttribute('src') === targetSrc) {
      return;
    }

    const nextIndex = visibleIndex === 0 ? 1 : 0;
    const next = images[nextIndex];

    clearTimeout(swapTimer);

    const showNext = () => {
      next.classList.add('is-visible');
      current.classList.remove('is-visible');
      visibleIndex = nextIndex;
    };

    if (reduceMotion) {
      next.src = targetSrc;
      showNext();
      return;
    }

    const preloader = new Image();
    preloader.onload = () => {
      next.src = targetSrc;
      showNext();
    };
    preloader.onerror = () => {
      next.src = defaultSrc;
      showNext();
    };
    preloader.src = targetSrc;
  }

  function restoreDefaultOrActive() {
    setPreview(activePreviewSrc);
  }

  nav.addEventListener(
    'pointerenter',
    (event) => {
      if (suppressPreviewEvents || drawer.classList.contains('gm-drawer--subpanel-open')) return;
      const link = event.target instanceof Element ? event.target.closest('[data-preview-src]') : null;
      if (!link || !nav.contains(link)) return;
      const src = link.getAttribute('data-preview-src');
      if (src) setPreview(src);
    },
    true
  );

  nav.addEventListener(
    'focusin',
    (event) => {
      if (suppressPreviewEvents || drawer.classList.contains('gm-drawer--subpanel-open')) return;
      const link = event.target instanceof Element ? event.target.closest('[data-preview-src]') : null;
      if (!link || !nav.contains(link)) return;
      const src = link.getAttribute('data-preview-src');
      if (src) setPreview(src);
    },
    true
  );

  nav.addEventListener('pointerleave', () => {
    if (suppressPreviewEvents || drawer.classList.contains('gm-drawer--subpanel-open')) return;
    swapTimer = window.setTimeout(restoreDefaultOrActive, 80);
  });

  nav.addEventListener('focusout', (event) => {
    if (suppressPreviewEvents || drawer.classList.contains('gm-drawer--subpanel-open')) return;
    if (event.relatedTarget instanceof Node && nav.contains(event.relatedTarget)) return;
    swapTimer = window.setTimeout(restoreDefaultOrActive, 80);
  });

  const details = drawer.closest('details');
  if (details) {
    details.addEventListener('toggle', () => {
      if (!details.open) return;
      restoreDefaultOrActive();
    });
  }

  images[0].src = activePreviewSrc || defaultSrc;
  images[0].classList.add('is-visible');
  images[1].src = defaultSrc;

  if (transitionMs === 0) {
    images.forEach((img) => {
      img.style.transition = 'none';
    });
  }

  return { freezePreview, restoreFrozenPreview, clearPreviewTimers };
}

/**
 * Abre o details do app drawer (atributo open + classe menu-open + a11y).
 * @param {HTMLElement} drawer
 * @returns {boolean}
 */
function ensureDrawerOpen(drawer) {
  const details = drawer.closest('details.menu-drawer-container');
  const headerDrawer = drawer.closest('header-drawer');
  if (!(details instanceof HTMLDetailsElement)) return false;

  if (!details.hasAttribute('open')) {
    details.setAttribute('open', '');
  }

  if (!details.classList.contains('menu-open')) {
    if (headerDrawer && 'open' in headerDrawer && typeof headerDrawer.open === 'function') {
      headerDrawer.open();
    } else {
      const summary = details.querySelector('summary');
      summary?.setAttribute('aria-expanded', 'true');
      details.classList.add('menu-open');
    }
  }

  return true;
}

/**
 * @param {(panelId: string) => void} openPanel
 */
function initDesktopNavTriggers(openPanel) {
  document.querySelectorAll('[data-gm-desktop-drawer-panel]').forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      const panelId = desktopTriggerPanel(trigger);
      if (panelId) openPanel(panelId);
    });
  });
}

/**
 * @param {string | null} activePanelId
 */
function syncDesktopNavAria(activePanelId) {
  document.querySelectorAll('[data-gm-desktop-drawer-panel]').forEach((trigger) => {
    const panelId = desktopTriggerPanel(trigger);
    const isActive = Boolean(activePanelId && panelId === activePanelId);
    trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
  });
}

/**
 * @param {HTMLElement} drawer
 * @param {Element} drawerRoot
 * @param {number} panelTransitionMs
 * @param {{ freezePreview: () => void, restoreFrozenPreview: () => void, clearPreviewTimers: () => void }} preview
 * @returns {{ openPanel: (panelId: string) => void }}
 */
function initPanels(drawer, drawerRoot, panelTransitionMs, preview) {
  const layers = drawer.querySelector('.gm-drawer-layers');
  const subpanels = layers
    ? /** @type {HTMLElement[]} */ ([...layers.querySelectorAll('.gm-drawer-subpanel')])
    : /** @type {HTMLElement[]} */ ([...drawerRoot.querySelectorAll('.gm-drawer-subpanel')]);
  const openButtons = drawer.querySelectorAll('[data-gm-drawer-panel-open]');
  const headerClose = drawer.querySelector('[data-gm-drawer-header-close]');
  const headerTitle = drawer.querySelector('[data-gm-drawer-header-title]');
  const headerLogo = drawer.querySelector('.menu-drawer__header-logo');
  const headerDrawer = drawer.closest('header-drawer');
  const details = drawer.closest('details');
  const scope = layers ?? drawerRoot;

  const closeLabel = headerClose?.getAttribute('data-gm-drawer-close-label') || 'Fechar';
  const backLabel = headerClose?.getAttribute('data-gm-drawer-back-label') || 'Voltar ao menu';
  const reduceMotion = panelTransitionMs === 0;
  const headerBrandMs = reduceMotion ? 0 : 320;
  const headerBrandStaggerMs = reduceMotion ? 0 : 70;
  const headerTitleSwapMs = reduceMotion ? 0 : 200;

  /** @type {HTMLElement | null} */
  let openPanelEl = null;
  /** @type {HTMLElement[]} */
  let panelStack = [];
  /** @type {Map<HTMLElement, number>} */
  const closeTimers = new Map();
  /** @type {number} */
  let headerBrandTimer = 0;

  function clearHeaderBrandTimer() {
    if (headerBrandTimer) {
      window.clearTimeout(headerBrandTimer);
      headerBrandTimer = 0;
    }
  }

  /**
   * @param {HTMLElement} panel
   */
  function syncHeaderPanelTitle(panel) {
    if (!(headerTitle instanceof HTMLElement)) return;

    const panelTitle = panel.querySelector('.gm-drawer-subpanel__title');
    const titleText = panelTitle?.textContent?.trim() || '';
    const isBrandOpen = drawer.classList.contains('gm-drawer--subpanel-open');
    const previousText = headerTitle.textContent.trim();

    clearHeaderBrandTimer();
    headerTitle.classList.remove('is-swapping');
    headerTitle.classList.remove('is-entering');
    headerTitle.classList.remove('is-title-swap-in');
    headerTitle.classList.remove('is-closing');
    if (headerLogo instanceof HTMLElement) {
      headerLogo.style.transition = '';
      headerLogo.style.opacity = '';
      headerLogo.style.transform = '';
      headerLogo.classList.remove('is-holding-hidden');
    }
    drawer.classList.remove('gm-drawer-header-brand-closing');
    drawer.classList.remove('gm-drawer-header-brand-closing--reveal');

    if (!isBrandOpen) {
      headerTitle.textContent = titleText;
      headerTitle.setAttribute('aria-hidden', 'false');
      panel.setAttribute('aria-labelledby', headerTitle.id || 'gm-drawer-header-panel-title');

      if (!reduceMotion) {
        void headerTitle.offsetWidth;
      }
      drawer.classList.add('gm-drawer--subpanel-open');
    } else if (previousText !== titleText) {
      headerTitle.classList.add('is-swapping');
      if (!reduceMotion) {
        void headerTitle.offsetWidth;
      }
      headerBrandTimer = window.setTimeout(() => {
        headerTitle.textContent = titleText;
        headerTitle.classList.remove('is-swapping');
        if (!reduceMotion) {
          headerTitle.classList.add('is-entering');
          void headerTitle.offsetWidth;
          headerTitle.classList.remove('is-entering');
          headerTitle.classList.add('is-title-swap-in');
          void headerTitle.offsetWidth;
          headerBrandTimer = window.setTimeout(() => {
            headerTitle.classList.remove('is-title-swap-in');
            headerBrandTimer = 0;
          }, headerTitleSwapMs);
        } else {
          headerBrandTimer = 0;
        }
      }, headerTitleSwapMs);
      panel.setAttribute('aria-labelledby', headerTitle.id || 'gm-drawer-header-panel-title');
      return;
    }

    if (headerLogo instanceof HTMLElement) {
      headerLogo.setAttribute('aria-hidden', 'true');
    }
  }

  function clearHeaderPanelTitle() {
    if (!(headerTitle instanceof HTMLElement)) return;

    clearHeaderBrandTimer();
    headerTitle.classList.remove('is-swapping');
    headerTitle.classList.remove('is-entering');
    headerTitle.classList.remove('is-title-swap-in');

    if (!drawer.classList.contains('gm-drawer--subpanel-open')) {
      headerTitle.classList.remove('is-closing');
      drawer.classList.remove('gm-drawer-header-brand-closing');
      drawer.classList.remove('gm-drawer-header-brand-closing--reveal');
      headerTitle.textContent = '';
      headerTitle.setAttribute('aria-hidden', 'true');
      subpanels.forEach((panel) => panel.removeAttribute('aria-labelledby'));
      if (headerLogo instanceof HTMLElement) {
        headerLogo.style.transition = '';
        headerLogo.style.opacity = '';
        headerLogo.style.transform = '';
        headerLogo.classList.remove('is-holding-hidden');
        headerLogo.setAttribute('aria-hidden', 'false');
      }
      return;
    }

    if (headerLogo instanceof HTMLElement) {
      headerLogo.setAttribute('aria-hidden', 'true');
      headerLogo.classList.add('is-holding-hidden');
    }

    function finishHeaderBrandClose() {
      headerTitle.classList.remove('is-closing');
      if (headerLogo instanceof HTMLElement) {
        headerLogo.style.transition = '';
        headerLogo.style.opacity = '';
        headerLogo.style.transform = '';
        headerLogo.classList.remove('is-holding-hidden');
      }
      drawer.classList.remove('gm-drawer-header-brand-closing');
      drawer.classList.remove('gm-drawer-header-brand-closing--reveal');

      if (!drawer.classList.contains('gm-drawer--subpanel-open') && !openPanelEl) {
        headerTitle.textContent = '';
        headerTitle.setAttribute('aria-hidden', 'true');
        subpanels.forEach((panel) => panel.removeAttribute('aria-labelledby'));
      }

      if (headerLogo instanceof HTMLElement && !openPanelEl) {
        headerLogo.setAttribute('aria-hidden', 'false');
      }

      headerBrandTimer = 0;
    }

    if (reduceMotion) {
      drawer.classList.remove('gm-drawer--subpanel-open');
      finishHeaderBrandClose();
      return;
    }

    drawer.classList.add('gm-drawer-header-brand-closing');
    headerTitle.classList.add('is-closing');
    void headerTitle.offsetWidth;
    drawer.classList.remove('gm-drawer--subpanel-open');
    void headerTitle.offsetWidth;

    headerBrandTimer = window.setTimeout(() => {
      if (headerLogo instanceof HTMLElement) {
        headerLogo.classList.remove('is-holding-hidden');
        headerLogo.style.transition = `opacity ${headerBrandMs}ms cubic-bezier(0.4, 0, 0.2, 1), transform ${headerBrandMs}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        headerLogo.style.opacity = '0';
        headerLogo.style.transform = 'translate3d(0, -5px, 0) scale(0.96)';
        void headerLogo.offsetWidth;
        drawer.classList.add('gm-drawer-header-brand-closing--reveal');
        headerLogo.style.opacity = '1';
        headerLogo.style.transform = 'translate3d(0, 0, 0) scale(1)';
        void headerLogo.offsetWidth;
      } else {
        drawer.classList.add('gm-drawer-header-brand-closing--reveal');
      }

      headerBrandTimer = window.setTimeout(() => {
        finishHeaderBrandClose();
      }, headerBrandMs);
    }, headerBrandMs);
  }

  function updateHeaderPanelState() {
    if (headerClose instanceof HTMLButtonElement) {
      const isBack = Boolean(openPanelEl);
      headerClose.classList.toggle('is-back-mode', isBack);
      headerClose.setAttribute('aria-label', isBack ? backLabel : closeLabel);
    }

    if (openPanelEl) {
      syncHeaderPanelTitle(openPanelEl);
      return;
    }

    clearHeaderPanelTitle();
  }

  /**
   * @param {HTMLElement} panel
   */
  function closePanel(panel) {
    const pending = closeTimers.get(panel);
    if (pending) window.clearTimeout(pending);

    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');

    const timer = window.setTimeout(() => {
      if (!panel.classList.contains('is-open')) {
        panel.setAttribute('hidden', '');
      }
      closeTimers.delete(panel);
    }, panelTransitionMs);

    closeTimers.set(panel, timer);
  }

  function closeAllPanels() {
    subpanels.forEach((panel) => closePanel(panel));
    openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    panelStack = [];
    openPanelEl = null;
    preview.clearPreviewTimers();
    preview.restoreFrozenPreview();
    updateHeaderPanelState();
    syncDesktopNavAria(null);
  }

  function closeCurrentPanel() {
    if (!openPanelEl) return;

    const closing = openPanelEl;
    const panelId = closing.getAttribute('data-gm-drawer-panel');

    closePanel(closing);
    panelStack = panelStack.filter((panel) => panel !== closing);

    if (panelId) {
      const trigger = drawer.querySelector(`[data-gm-drawer-panel-open="${panelId}"]`);
      if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    }

    openPanelEl = panelStack.length > 0 ? panelStack[panelStack.length - 1] : null;

    if (openPanelEl) {
      updateHeaderPanelState();
      syncDesktopNavAria(getDesktopRootPanelId(openPanelEl));
      if (panelId) {
        const trigger = drawer.querySelector(`[data-gm-drawer-panel-open="${panelId}"]`);
        if (trigger instanceof HTMLElement) {
          window.setTimeout(() => trigger.focus(), panelTransitionMs || 0);
        }
      }
      return;
    }

    preview.clearPreviewTimers();
    preview.restoreFrozenPreview();
    updateHeaderPanelState();
    syncDesktopNavAria(null);

    if (panelId) {
      const trigger = drawer.querySelector(`[data-gm-drawer-panel-open="${panelId}"]`);
      if (trigger instanceof HTMLElement) {
        trigger.focus();
      }
    }
  }

  /**
   * @param {string} panelId
   */
  function openSubpanel(panelId) {
    ensureDrawerOpen(drawer);

    preview.clearPreviewTimers();
    if (panelStack.length === 0) {
      preview.freezePreview();
    }

    const panel = scope.querySelector(`.gm-drawer-subpanel[data-gm-drawer-panel="${panelId}"]`);
    if (!(panel instanceof HTMLElement)) return;

    const parentId = panel.getAttribute('data-gm-drawer-panel-parent');

    if (parentId) {
      const parent = scope.querySelector(`.gm-drawer-subpanel[data-gm-drawer-panel="${parentId}"]`);
      if (parent instanceof HTMLElement && !parent.classList.contains('is-open')) {
        openSubpanel(parentId);
      }

      subpanels.forEach((item) => {
        const itemParent = item.getAttribute('data-gm-drawer-panel-parent');
        if (item !== panel && itemParent === parentId) {
          closePanel(item);
          panelStack = panelStack.filter((stackPanel) => stackPanel !== item);
        }
      });
    } else {
      subpanels.forEach((item) => closePanel(item));
      openButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      panelStack = [];
    }

    const pending = closeTimers.get(panel);
    if (pending) window.clearTimeout(pending);

    panel.removeAttribute('hidden');
    panel.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => panel.classList.add('is-open'));

    openButtons.forEach((button) => {
      const isTrigger = button.getAttribute('data-gm-drawer-panel-open') === panelId;
      button.setAttribute('aria-expanded', isTrigger ? 'true' : 'false');
    });

    panelStack = panelStack.filter((stackPanel) => stackPanel !== panel);
    panelStack.push(panel);
    openPanelEl = panel;
    updateHeaderPanelState();
    syncDesktopNavAria(getDesktopRootPanelId(panel));

    if (headerClose instanceof HTMLElement) {
      window.setTimeout(() => headerClose.focus(), panelTransitionMs || 0);
    }
  }

  /**
   * @param {HTMLElement} panel
   * @returns {string | null}
   */
  function getDesktopRootPanelId(panel) {
    let rootId = panel.getAttribute('data-gm-drawer-panel');
    let parentId = panel.getAttribute('data-gm-drawer-panel-parent');

    while (parentId) {
      rootId = parentId;
      const parent = scope.querySelector(`.gm-drawer-subpanel[data-gm-drawer-panel="${parentId}"]`);
      parentId = parent instanceof HTMLElement ? parent.getAttribute('data-gm-drawer-panel-parent') : null;
    }

    return rootId;
  }

  openButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      /* Só intercepta clique esquerdo simples: middle-click / ctrl+click / menu
         de contexto no <a> continuam nativos. */
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const panelId = button.getAttribute('data-gm-drawer-panel-open');
      if (!panelId) return;

      event.preventDefault();
      openSubpanel(panelId);
    });
  });

  if (headerClose instanceof HTMLButtonElement) {
    headerClose.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (openPanelEl) {
        const parentId = openPanelEl.getAttribute('data-gm-drawer-panel-parent');
        if (parentId) {
          closeCurrentPanel();
          return;
        }

        const panelId = openPanelEl.getAttribute('data-gm-drawer-panel');
        closeAllPanels();
        if (panelId) {
          const trigger = drawer.querySelector(`[data-gm-drawer-panel-open="${panelId}"]`);
          if (trigger instanceof HTMLElement) trigger.focus();
        }
        return;
      }

      if (headerDrawer && 'close' in headerDrawer && typeof headerDrawer.close === 'function') {
        headerDrawer.close();
      }
    });
  }

  if (headerDrawer instanceof HTMLElement) {
    headerDrawer.addEventListener(
      'keyup',
      (event) => {
        if (event.key !== 'Escape' || !openPanelEl) return;
        event.stopPropagation();
        event.preventDefault();

        const parentId = openPanelEl.getAttribute('data-gm-drawer-panel-parent');
        if (parentId) {
          closeCurrentPanel();
          return;
        }

        const panelId = openPanelEl.getAttribute('data-gm-drawer-panel');
        closeAllPanels();
        if (panelId) {
          const trigger = drawer.querySelector(`[data-gm-drawer-panel-open="${panelId}"]`);
          if (trigger instanceof HTMLElement) trigger.focus();
        }
      },
      true
    );
  }

  details?.addEventListener('toggle', () => {
    if (!details.open) {
      closeAllPanels();
      syncDesktopNavAria(null);
    }
  });

  updateHeaderPanelState();

  function closeDrawer() {
    closeAllPanels();
    syncDesktopNavAria(null);
    if (headerDrawer && 'close' in headerDrawer && typeof headerDrawer.close === 'function') {
      headerDrawer.close();
      return;
    }
    if (details instanceof HTMLDetailsElement && details.open) {
      details.open = false;
      details.classList.remove('menu-open');
    }
  }

  return { openPanel: openSubpanel, closeDrawer };
}

/** Abre o modal de busca ao interagir com o input do drawer. */
function initDrawerSearch(drawer) {
  const input = drawer.querySelector('[data-gm-drawer-search]');
  if (!(input instanceof HTMLInputElement)) return;

  const openSearch = () => {
    const searchModal = document.getElementById('search-modal');
    if (searchModal && 'showDialog' in searchModal && typeof searchModal.showDialog === 'function') {
      searchModal.showDialog();
    }

    requestAnimationFrame(() => {
      const modalInput = document.querySelector('#search-modal .search-input');
      if (modalInput instanceof HTMLInputElement) {
        modalInput.focus();
      }
    });
  };

  input.addEventListener('click', (event) => {
    event.preventDefault();
    openSearch();
  });

  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      openSearch();
    }
  });
}
